import React, { useEffect } from 'react'
import { toast } from 'react-toastify'
import { Parser } from '@json2csv/plainjs'
import { Box, Grid, MenuItem, Select, TextField } from '@mui/material'
import Button from '@mui/material/Button'
import { styled } from '@mui/material/styles'
import { DataGrid, GridColDef } from '@mui/x-data-grid'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker'
import { useMutation } from '@tanstack/react-query'
import { useQuery } from '@tanstack/react-query'
import RewardsAPI from 'api/faber/RewardsAPI'
import { Form, Formik } from 'formik'
import OrbitSession from 'hooks/useCookies'
import * as Yup from 'yup'

import NormalText from '../../../../common/Typography/NormalText'

const CustomDatePicker = (props: DatePickerProps) => {
  const { label, disabled, date, setDate } = props

  const handleStartDate = (newValue: Date | null) => {
    setDate(newValue)
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <DateTimePicker
        label={label}
        inputFormat='dd-MM-yyyy HH:00'
        views={['year', 'month', 'day', 'hours']}
        value={date}
        onChange={handleStartDate}
        renderInput={(params) => (
          <TextField placeholder='Date' variant='standard' {...params} />
        )}
        disabled={disabled}
      />
    </LocalizationProvider>
  )
}

export default CustomDatePicker

interface DatePickerProps {
  label: string
  disabled: boolean
  date: Date | null
  setDate: (value: Date | null) => void
}

const formikInitialValues = {
  distributorId: '',
  promocodeSummary: '',
  rewardProductId: '',
  maxClaims: 2,
  maxConsumption: 1,
  validityStartAt: 0,
  validityEndAt: 0,
  batchSize: 0,
  promoRule: []
}

const NewDistributorPromoSchema = Yup.object().shape({
  distributorId: Yup.string().required('Distributor ID is required'),
  promocodeSummary: Yup.string().required(
    'Distributor promo summary is required'
  ),
  maxClaims: Yup.number()
    .min(1, 'Max claims must be at least 1')
    .required('Max claims is required'),
  maxConsumption: Yup.number()
    .min(1, 'Max consumption must be at least 1')
    .required('Max consumption is required'),
  batchSize: Yup.number()
    .min(1, 'Batch size must be at least 1')
    .max(1000, 'Batch size must be at most 1000')
    .required('Batch size is required'),
})

const CenteredBox = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  width: '80%'
})
const CenteredGrid = styled(Grid)({
  display: 'flex',
  alignItems: 'center'
})

const StyledTextField = styled(TextField)({
  '& input': {
    padding: '6px'
  },
  width: '100%'
})

const startDate = new Date()
startDate.setMinutes(0)

const endDate = new Date()
endDate.setMinutes(0)

export const NewDistributorPromoForm = ({
  handleClose
}: {
  handleClose: () => void
}) => {
  const [validityStartAt, setValidityStart] = React.useState<Date | null>(
    startDate
  )
  const [validityEndAt, setValidityEnd] = React.useState<Date | null>(endDate)
  const [isButtonLoading, setIsButtonLoading] = React.useState(false)

  interface Row {
    id: string
    ruleName: string
    ruleDescription: string
    ruleType: string
  }
  interface RewardRow {
    id: string
    reward_name: string
    reward_description: string
    reward_description_internal: string
  }
  const [rows, setRows] = React.useState<Row[]>([])
  const [selectionModel, setSelectionModel] = React.useState<string[]>([])
  const [rewardRows, setRewardRows] = React.useState<RewardRow[]>([])
  const [rewardSelectionModel, setRewardSelectionModel] =
    React.useState<string>('')

  const faberToken = OrbitSession.get('faberToken')
  const { data: distributors } = useQuery(['getDistributors'], async () =>
    RewardsAPI.getDistributors(faberToken)
  )
  const { data: rewardProducts } = useQuery(['getRewardProducts'], async () =>
    RewardsAPI.getRewardProducts(faberToken)
  )

  const { data: rewardRules } = useQuery(['getRewardRules'], async () =>
    RewardsAPI.getRewardRules(faberToken)
  )

  const rewardColumns: GridColDef[] = [
    {
      field: 'reward_name',
      headerName: 'Name',
      width: 350
    },
    {
      field: 'reward_description',
      headerName: 'Description',
      width: 350
    },
    {
      field: 'reward_description_internal',
      headerName: 'Internal description',
      width: 350
    }
  ]

  const columns: GridColDef[] = [
    {
      field: 'ruleName',
      headerName: 'Rule name',
      width: 350
    },
    {
      field: 'ruleDescription',
      headerName: 'Rule description',
      width: 350
    },
    {
      field: 'ruleType',
      headerName: 'Rule type',
      width: 200
    },
    {
      field: 'ruleValue',
      headerName: 'Rule value',
      width: 200
    }
  ]

  useEffect(() => {
    if (rewardProducts) {
      setRewardRows(rewardProducts)
    }
  }, [rewardProducts])

  useEffect(() => {
    if (rewardRules) {
      setRows(rewardRules)
    }
  }, [rewardRules])

  const downloadCSV = (jsonData) => {
    const json2csvParser = new Parser()
    const csvData = json2csvParser.parse(jsonData)
    const blob = new Blob([csvData], { type: 'text/csv' })
    const link = document.createElement('a')
    link.href = URL.createObjectURL(blob)
    link.download = 'data.csv' // Set the file name
    link.click()
  }

  const { mutate: createDistributorPromoCode } = useMutation(
    RewardsAPI.createDistributorPromo,
    {
      onSuccess: (res) => {
        handleClose()
        toast.success('Distributor code created successfully')
        downloadCSV(res.data.data)
      },
      onError: () => {
        toast.error('Error creating promo code')
        setIsButtonLoading(false)
      }
    }
  )


  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleFormSubmit = (values: NewDistributorPromoFormikProps) => {
    setIsButtonLoading(true)
    const allValues = {
      distributorId: values.distributorId,
      promocodeSummary: values.promocodeSummary,
      rewardProductId: rewardSelectionModel,
      maxClaims: values.maxClaims,
      maxConsumption: values.maxConsumption,
      validityStartAt: values.validityStartAt,
      validityEndAt: values.validityEndAt,
      batchSize: values.batchSize,
      promoRule: selectionModel,
      faberToken: faberToken
    }
    createDistributorPromoCode(allValues)
  }

  const rowIdToRuleId = rows.reduce((acc, row) => {
    acc[row.id] = row.id
    return acc
  }, {})

  /**
   * Event handlers
   */
  const handleUsageTypeSelect = (
    e,
    setFieldValue: (
      field: string,
      value,
      shouldValidate?: boolean | undefined
    ) => void
  ) => {
    setFieldValue(e.target.name, e.target.value)
  }

  return (
    <Formik
      initialValues={formikInitialValues}
      onSubmit={handleFormSubmit}
      validationSchema={NewDistributorPromoSchema}
    >
      {({
        submitForm,
        handleChange,
        errors,
        touched,
        handleBlur,
        setFieldValue
      }) => (
        <Form>
          <Grid container rowSpacing={2}>
            <Grid item xs={3}>
              <NormalText isBold>Distributor</NormalText>
            </Grid>
            <Grid item xs={9}>
              <Select
                defaultValue=''
                variant='standard'
                sx={{ width: '80%' }}
                onChange={(e) => handleUsageTypeSelect(e, setFieldValue)}
                name='distributorId'
                label=''
              >
                {distributors &&
                  distributors.map((distributor) => (
                    <MenuItem key={distributor.id} value={distributor.id}>
                      {distributor.distributorName}
                    </MenuItem>
                  ))}
              </Select>
            </Grid>
            <Grid item xs={3}>
              <NormalText isBold>Promo Name</NormalText>
            </Grid>
            <Grid item xs={9}>
              <Box sx={{ width: '80%' }}>
                <StyledTextField
                  name='promocodeSummary'
                  onChange={(e) => {
                    handleBlur(e)
                    handleChange(e)
                  }}
                  inputProps={{
                    'data-testid': 'promocode-summary'
                  }}
                  fullWidth
                  variant='outlined'
                  error={
                    !!(errors.promocodeSummary && touched.promocodeSummary)
                  }
                  helperText={
                    errors.promocodeSummary && touched.promocodeSummary
                      ? errors.promocodeSummary
                      : null
                  }
                />
              </Box>
            </Grid>
            <Grid item xs={3}>
              <NormalText isBold>Reward Product</NormalText>
            </Grid>
            <Grid item xs={12} height={640}>
              <DataGrid
                rows={rewardRows}
                columns={rewardColumns}
                rowsPerPageOptions={[10]}
                pageSize={10}
                selectionModel={rewardSelectionModel}
                onSelectionModelChange={(newSelection) => {
                  setRewardSelectionModel(newSelection[0] as string)
                }}
              />
            </Grid>
            <Grid item xs={3}>
              <NormalText isBold>Number of Promo Codes</NormalText>
            </Grid>
            <CenteredGrid item xs={9}>
              <CenteredBox>
                <StyledTextField
                  name='batchSize'
                  type='number'
                  inputProps={{
                    min: 0,
                    max: 1000
                  }}
                  onChange={(e) => {
                    handleBlur(e)
                    handleChange(e)
                  }}
                  error={!!(errors.batchSize && touched.batchSize)}
                  helperText={
                    errors.batchSize && touched.batchSize
                      ? errors.batchSize
                      : null
                  }
                />
              </CenteredBox>
            </CenteredGrid>
            <Grid item xs={3}>
              <NormalText isBold>Claims Per Promo Code</NormalText>
            </Grid>
            <CenteredGrid item xs={9}>
              <CenteredBox>
                <StyledTextField
                  name='maxClaims'
                  type='number'
                  inputProps={{
                    min: 1
                  }}
                  onChange={(e) => {
                    handleBlur(e)
                    handleChange(e)
                  }}
                  error={!!(errors.maxClaims && touched.maxClaims)}
                  helperText={
                    errors.maxClaims && touched.maxClaims
                      ? errors.maxClaims
                      : null
                  }
                />
              </CenteredBox>
            </CenteredGrid>
            <Grid item xs={3}>
              <NormalText isBold>Consumptions Per Promo Code</NormalText>
            </Grid>
            <CenteredGrid item xs={9}>
              <CenteredBox>
                <StyledTextField
                  name='maxConsumption'
                  type='number'
                  inputProps={{
                    min: 1
                  }}
                  onChange={(e) => {
                    handleBlur(e)
                    handleChange(e)
                  }}
                  error={!!(errors.maxConsumption && touched.maxConsumption)}
                  helperText={
                    errors.maxConsumption && touched.maxConsumption
                      ? errors.maxConsumption
                      : null
                  }
                />
              </CenteredBox>
            </CenteredGrid>
            <Grid item xs={3}>
              <NormalText isBold>Validity Start Date</NormalText>
            </Grid>
            <Grid item xs={9}>
              <CustomDatePicker
                label='Validity Start Date'
                date={validityStartAt}
                setDate={(value: Date | null) => {
                  setValidityStart(value)
                  // Convert the Date object to Unix time
                  const unixTime = value ? Math.floor(value.getTime()) : null
                  setFieldValue('validityStartAt', unixTime)
                }}
                disabled={false}
              />
            </Grid>
            <Grid item xs={3}>
              <NormalText isBold>Validity End Date</NormalText>
            </Grid>
            <Grid item xs={9}>
              <CustomDatePicker
                label='Validity End Date'
                date={validityEndAt}
                setDate={(value: Date | null) => {
                  setValidityEnd(value)
                  // Convert the Date object to Unix time
                  const unixTime = value ? Math.floor(value.getTime()) : null
                  setFieldValue('validityEndAt', unixTime)
                }}
                disabled={false}
              />
            </Grid>
            <Grid item xs={3}>
              <NormalText isBold>Reward Rules</NormalText>
            </Grid>
            <Grid item xs={12} height={400}>
              <DataGrid
                rows={rows}
                columns={columns}
                checkboxSelection
                rowsPerPageOptions={[5]}
                pageSize={5}
                selectionModel={selectionModel}
                onSelectionModelChange={(newSelection) => {
                  const selectedRuleIds = newSelection.map(
                    (rowId) => rowIdToRuleId[rowId]
                  )
                  setSelectionModel(selectedRuleIds)
                }}
              />
            </Grid>
          </Grid>
          <Grid>
            <Box mt={3}>
              {' '}
              {/* Adjust the number to increase or decrease the space */}
              <Button disabled={isButtonLoading} onClick={submitForm}>
                Save changes
              </Button>
            </Box>
          </Grid>
        </Form>
      )}
    </Formik>
  )
}
interface NewDistributorPromoFormikProps {
  distributorId: string
  promocodeSummary: string
  rewardProductId: string
  maxClaims: number
  maxConsumption: number
  validityStartAt: number
  validityEndAt: number
  batchSize: number
  promoRule: string[]
}
